import React, { useEffect, useMemo, useState } from 'react';
import { MonitorSelectType } from '../../types';
import NewRegionSelect from '@/pages/home/components/NewRegionSelect';
import { monitorLocale } from '../../constants/monitor';
import useDefaultTwoLevelFilter from '../../hooks/useDefaultTwoLevelFilter';
import { commonBackInfo } from '../../utils';
import { useIntl } from 'umi';
import BubbleCommon, { filterMonitorWayTipText } from '../../../BubbleCommon';
import useWindowTipsShow from '../../hooks/useWindowTipsShow';
import { SPECIAL_ACCOUNT_KEY } from './config';
import { addFilterBuriedPoint } from '../../utils/point';
import { FilterButtonPoint } from '@op/operationLog/pages/filter';
import { AllMonitorType, buildAccountGroupMap, useButtonElement, useSelectOptions } from './hooks';
import './index.scss';
import { serversSplitText } from '../../utils/monitorUtil';
import ContentHeader, {
  MonitorRuleType,
  ruleOptionsMap,
} from './ContentHeader';
// import { privateSourceGameIds } from '@/pages/opinion/pages/feeds/constants';

type MonitorKeyType =
  | 'social_media_by_keyword'
  | 'social_media_by_account'
  | 'external_data_sources';

/**
 * 获取 except 数据：当 99999 + except 时，还原为 all - except
 */
function getExceptData(
  key: MonitorKeyType,
  data: any,
  all: Array<string>,
): string[] | undefined {
  const selectData = data[key] || [];
  const exceptData = data[`except_${key}`] || [];

  if (
    selectData.length === 1 &&
    selectData[0] === '99999' &&
    exceptData.length > 0
  ) {
    return all.filter((item: string) => !exceptData.includes(item));
  }
}

/**
 * 将 account_categories 展开为二级 value 列表（用于 UI 回显）
 */
function expandAccountCategories(
  accountCategories: string[],
  exceptList: string[],
  groupMap: Record<string, { all: string[] }>,
  accountToGroup: Record<string, string>,
): string[] {
  const expanded: string[] = [];

  accountCategories.forEach((groupKey) => {
    const allInGroup = groupMap[groupKey]?.all || [];

    const exceptInGroup = exceptList.filter(
      (v) => accountToGroup[v] === groupKey,
    );

    const selectedInGroup = allInGroup.filter(
      (v) => !exceptInGroup.includes(v),
    );

    expanded.push(...selectedInGroup);
  });

  return expanded;
}

// 如果通过defaultValue传入一个selectAllAccount，那么就直接选中所有的官号，不做特殊过滤处理
// isNightingale：是feeds页面并且是Nightingale类型的游戏
export default function MonitorSelect({
  onChange,
  defaultValue,
  filterValue,
  defaultKey,
  filterOptions,
  oriDefaultFilter,
  backOriDefaultValue,
  lang,
  onlyAccount = false,
  isNightingale = false,
  isDiscordPrivate = false,
  returnAllValue = false,
  defaultSelected,
  boxClassName,
}: MonitorSelectType) {
  const { formatMessage } = useIntl();
  // 监控方式筛选规则状态
  const [monitorRule, setMonitorRule] = useState<MonitorRuleType>(
    defaultValue?.monitor_source_match_mode || 'include',
  );

  const {
    monitoringOptions,
    allKeyword,
    discordPrivateOptions,
    allAccount,
    allExternalList,
  } = useSelectOptions({
    filterOptions,
    onlyAccount,
    isDiscordPrivate,
    isNightingale,
  });

  const dealwithAllValue = (
    options: any,
    auxiliaryInfo: { account: boolean; keyword: boolean; external: boolean },
  ) => {
    const markets: Array<string> = [];
    const regions: Array<string> = [];
    const { keyword, account, external } = auxiliaryInfo;

    options.forEach((item: { value: string; children: any[] }) => {
      const isKeyword = item.value === SPECIAL_ACCOUNT_KEY.KEYWORD;
      const isExternal = item.value === SPECIAL_ACCOUNT_KEY.EXTERNAL;

      const values = item.children.filter(() => {
        if (isKeyword) return keyword;
        if (isExternal) return external;
        return account;
      });

      const flag = isKeyword ? keyword : account;
      flag && regions.push(item.value);

      markets.push(...values.map((cItem) => cItem.value));
    });

    return {
      markets,
      regions,
      type: 'market',
    };
  };

  const firstExcludeOptions = useMemo(() => {
    return [
      ...allKeyword.uncheck,
      ...allAccount.uncheck,
      ...allExternalList.uncheck,
    ];
  }, [allKeyword, allAccount, allExternalList]);

  const {
    setSelectValue,
    selectValue,
    onChange: onMonitorChange,
    diffClassName,
    isFirst,
  } = useDefaultTwoLevelFilter({
    originalOptions: monitoringOptions || [],
    defaultValue,
    filterValue,
    oriDefaultFilter,
    onlySecondLevelValue: true,
    firstExcludeOptions,
    dealwithDefaultValue: (value: any) => {
      const result: {
        social_media_by_keyword: string[];
        social_media_by_account: string[];
        external_data_sources: string[];
        account_categories?: string[];
      } = {
        social_media_by_keyword: [],
        social_media_by_account: [],
        external_data_sources: [],
        account_categories: [],
      };

      // 构建分组映射表，用于 account_categories 的回显展开
      const { groupMap: defaultGroupMap, accountToGroup: defaultAccountToGroup } =
        buildAccountGroupMap(monitoringOptions || []);

      const monitorOptions: {
        key: MonitorKeyType;
        data: AllMonitorType;
        dealwith: (value: any, all: Array<string>) => Array<string>;
      }[] = [
          {
            key: 'social_media_by_keyword',
            data: allKeyword,
            dealwith: (data: any, all: Array<string>) => {
              const selectData = getExceptData(
                'social_media_by_keyword',
                data,
                all,
              );
              if (selectData) return selectData;
              return data.social_media_by_keyword || [];
            },
          },
          {
            key: 'social_media_by_account',
            data: allAccount,
            dealwith: (data: any, all: Array<string>) => {
              const selectData = getExceptData(
                'social_media_by_account',
                data,
                all,
              );
              const list =
                (selectData ? selectData : data.social_media_by_account) || [];
              // 因为改版，兼容老版本数据格式，这里需要补充一些数据
              const real_social_media_by_account = list
                .map((item: string) => {
                  if (item.includes(serversSplitText)) return item;
                  return allAccount.all.forEach((info: string) => {
                    const [_channel, value] = info.split(serversSplitText);
                    if (value === item) return info;
                  });
                })
                .flat(2);

              return real_social_media_by_account;
            },
          },
          {
            key: 'external_data_sources',
            data: allExternalList,
            dealwith: (data: any, all: Array<string>) => {
              const selectData = getExceptData(
                'external_data_sources',
                data,
                all,
              );
              if (selectData) return selectData;
              return data.external_data_sources || [];
            },
          },
        ];

      function getIsAll(key: MonitorKeyType) {
        const data = value[key] || [];
        const exceptData = value[`except_${key}`] || [];
        return (
          data.length === 1 && data[0] === '99999' && exceptData.length === 0
        );
      }
      function getIsNone(
        key: MonitorKeyType,
        _value: '-99999' | 'public' | 'uncheck' = '-99999',
      ) {
        const data = value[key] || [];
        if (data.length === 0 && _value === 'public') {
          // 当 account_categories 有值时，social_media_by_account 为空
          // 不代表"仅公开"，账户信息由 account_categories 表达
          const hasAccountCategories =
            key === 'social_media_by_account' &&
            Array.isArray(value.account_categories) &&
            value.account_categories.length > 0;
          if (hasAccountCategories) return false;
          return true;
        }
        return data.length === 1 && data[0] === _value;
      }

      function filterMonitorList(list: Array<string>, all: Array<string>) {
        return list.filter((item: string) => all.includes(item));
      }

      monitorOptions.forEach(({ key, data, dealwith }) => {
        const isAll = getIsAll(key);
        const isNone = getIsNone(key);
        const isPublic = getIsNone(key, 'public');
        const isUncheck = getIsNone(key, 'uncheck');

        const options = [
          { flag: isAll, data: data.all },
          { flag: isNone, data: [] },
          { flag: isPublic, data: data.public },
          { flag: isUncheck, data: data.uncheck },
        ];

        for (const { flag, data } of options) {
          if (flag) {
            result[key] = data;
            return;
          }
        }

        const list = dealwith(value, data.all);

        result[key] = filterMonitorList(list, data.all);
      });

      // 从 input value 中读取 account_categories
      const accountCategories = value.account_categories || [];
      if (Array.isArray(accountCategories) && accountCategories.length > 0) {
        result.account_categories = accountCategories;

        // 展开 account_categories 为二级 value 列表，合并到 social_media_by_account 中（用于 UI 回显）
        const exceptAccount = value.except_social_media_by_account || [];
        const expandedValues = expandAccountCategories(
          accountCategories,
          exceptAccount,
          defaultGroupMap,
          defaultAccountToGroup,
        );

        // 合并: expandedValues + result.social_media_by_account（去重）
        result.social_media_by_account = [
          ...new Set([...expandedValues, ...result.social_media_by_account]),
        ];
      }

      // TODO: 这个处理放到页面去通过dealwithFinallyCondData处理
      // 是Nightingale游戏 并且是DiscordPrivate Tab，就只要discordPrivateOptions数据
      if (isDiscordPrivate && isNightingale) {
        result.social_media_by_account = result.social_media_by_account.filter(
          (key: string) => discordPrivateOptions?.includes(key),
        );
        result.account_categories = [];
      }

      // TODO: 这个处理放到页面去通过dealwithFinallyCondData处理
      // 是Nightingale游戏 并且不是DiscordPrivate Tab，就只排除discordPrivateOptions数据
      if (!isDiscordPrivate && isNightingale) {
        result.social_media_by_account = result.social_media_by_account.filter(
          (key: string) => !discordPrivateOptions?.includes(key),
        );
      }

      const resultValue = onlyAccount
        ? result.social_media_by_account
        : [
          ...result.social_media_by_account,
          ...result.social_media_by_keyword,
          ...result.external_data_sources,
        ];

      return {
        value: resultValue,
        accountCategories: result.account_categories,
        auxiliaryInfo: {
          keyword: getIsAll('social_media_by_account'),
          account: getIsAll('social_media_by_keyword'),
          external: getIsAll('external_data_sources'),
        },
        type: 'market',
      };
    },
    dealwithAllValue,
    backOriDefaultValue: (oriData: { value: string[]; oneLevel: any[] }) => {
      const { data } = buttonElement(oriData, true);
      const value: any = {
        social_media_by_account: data?.account || [],
        social_media_by_keyword: data?.keyword || [],
        external_data_sources: data?.external || [],
      };
      backOriDefaultValue?.(value);
    },
  });

  // 监听 filterValue 变化更新 monitorRule
  // 利用 useDefaultTwoLevelFilter 已处理首次初始化，只需监听后续变化
  useEffect(() => {
    if (!isFirst.current && filterValue?.monitor_source_match_mode) {
      setMonitorRule(filterValue.monitor_source_match_mode || 'include');
    }
  }, [filterValue]);

  const { buttonElementMemo, buttonElement } = useButtonElement({
    lang,
    onMonitorChange,
    selectValue,
    onlyAccount,
    returnAllValue,
    allKeyword,
    allAccount,
    allExternalList,
    monitorRule,
    monitoringOptions,
  });

  const monitorChange = (tags: string) => {
    setSelectValue(tags);
  };

  useEffect(() => {
    const { data, buttonEle, isAll, isNone } = buttonElement(selectValue, true);

    let value: any = data;
    // 仅仅显示官号，就将keyword添加到except_social_media_by_keyword字段中
    if (onlyAccount) {
      value.except_social_media_by_keyword = allKeyword.all;
      value.social_media_by_keyword = ['-99999'];
      value.except_external_data_sources = allExternalList.all;
      value.external_data_sources = ['-99999'];
    }

    if (isNightingale) {
      if (isDiscordPrivate) {
        value = {
          social_media_by_account: discordPrivateOptions,
          social_media_by_keyword: ['-99999'],
          external_data_sources: ['-99999'],
          account_categories: [],
        };
      } else {
        value = {
          ...value,
          except_social_media_by_account: [
            ...discordPrivateOptions,
            ...(value?.except_social_media_by_account || []),
          ],
        };
      }
    }

    onChange?.(
      'monitor',
      commonBackInfo(
        { ...value, monitor_source_match_mode: monitorRule },
        {
          [defaultKey]: buttonEle,
          monitor_source_match_mode: ruleOptionsMap[monitorRule],
        },
        {
          needChangeEntity: false,
          isAll: isAll || isNone,
          defaultAllValue: ['99999'],
        },
      ),
    );
  }, [selectValue, formatMessage, monitorRule]);

  const { tips, onOpen, onClose } = useWindowTipsShow({
    tipsMap: filterMonitorWayTipText,
  });

  const diffClassNameMemo = useMemo(() => {
    if (diffClassName) return diffClassName;

    if (monitorRule !== (defaultValue?.monitor_source_match_mode || 'include'))
      return 'dbo-diff-filter';

    return '';
  }, [diffClassName, monitorRule]);

  return (
    <div className={`monitor-select-wrapper ${diffClassNameMemo}`}>
      <BubbleCommon placement="top" showIcon={false} tipsMap={tips}>
        <NewRegionSelect
          header={
            <ContentHeader
              monitorRule={monitorRule}
              setMonitorRule={setMonitorRule}
            />
          }
          locale={lang}
          radioOptions={[]}
          onOpen={onOpen}
          onClose={() => {
            onClose();
            try {
              const { data } = buttonElement(selectValue, true);
              addFilterBuriedPoint(FilterButtonPoint.monitor, data);
            } catch (error) { }
          }}
          value={selectValue}
          options={monitoringOptions}
          language={monitorLocale}
          button={buttonElementMemo}
          onChange={(value: any) => monitorChange(value)}
          closeOnScroll={true}
          defaultSelectedRegion={defaultSelected}
          boxClassName={`${boxClassName} dbo-monitor-dropdown-wrapper`}
          onCloseReginSelect={(value: any) => {
            monitorChange(value);
          }}
        ></NewRegionSelect>
      </BubbleCommon>
    </div>
  );
}
